// Tablas del modulo de integraciones (solo definiciones: la importa
// `schema.ts`; ver `lib/scripting/schema_graph.test.ts`).
//
// docs/analisis-integraciones.md:
//   - PLUGIN: codigo (`convex/lib/integrations/plugins.ts`).
//   - CONEXION (`integration_connections`): instancia de un plugin por empresa,
//     con credenciales cifradas (AES-256-GCM, `lib/integrations/crypto.ts`).
//   - TRIGGER (`integration_triggers`): evento del bus -> accion del plugin,
//     con args que admiten bindings `{{record.campo}}` y un filtro opcional
//     (mismas condiciones que las automatizaciones).
//   - EJECUCION (`integration_executions`): cada invocacion (trigger, script o
//     manual), con reintentos y respuesta truncada.
//
// Conexiones y triggers son de la EMPRESA (no de la sucursal): las
// credenciales son del workspace. El evento que dispara un trigger si respeta
// el alcance de datos maestros (ver dispatcher.ts).

import { v } from "convex/values";
import {
  internalControl,
  defineTableWithInternalControl,
} from "../../lib/shared/validators";
import { automationCondition } from "../collections/automations";

export const integrationConnectionStatus = v.union(
  v.literal("draft"),
  v.literal("active"),
  v.literal("error"),
  v.literal("disconnected"),
);

/** Valores simples de configuracion / args (JSON plano). */
const scalarRecord = v.record(v.string(), v.union(v.string(), v.number(), v.boolean(), v.null()));

export const validatorIntegrationConnection = v.object({
  pluginId: v.string(),
  pluginVersion: v.string(),
  name: v.string(),
  status: integrationConnectionStatus,
  /** Credenciales cifradas (`v1.<kid>.<iv>.<cifrado>`), "" si no hay. */
  credentials: v.string(),
  /** Nombres de las credenciales guardadas (para la UI; nunca el valor). */
  credentialKeys: v.array(v.string()),
  /** Configuracion no sensible (`configSchema` del plugin). */
  config: scalarRecord,
  lastError: v.optional(v.string()),
  lastErrorAt: v.optional(v.number()),
  connectedAt: v.optional(v.number()),
  lastUsedAt: v.optional(v.number()),
  /** Plugins con worker (MQTT): SHA-256 del token del worker (nunca el token). */
  workerTokenHash: v.optional(v.string()),
  /** Ultimos 4 caracteres del token, para reconocerlo en la UI. */
  workerTokenHint: v.optional(v.string()),
  workerTokenCreatedAt: v.optional(v.number()),
  internalControl,
});

export const integration_connections = defineTableWithInternalControl(
  validatorIntegrationConnection,
)
  .index("by_company_and_plugin", ["internalControl.companyId", "pluginId"])
  .index("by_company_and_status", ["internalControl.companyId", "status"]);

export const integrationTriggerEvent = v.union(
  v.literal("onCreate"),
  v.literal("onUpdate"),
  v.literal("onDelete"),
  v.literal("manual"),
  /** Mensaje MQTT recibido (source `mqtt`). */
  v.literal("onMessage"),
);

export const integrationTriggerSource = v.union(
  v.literal("system_table"),
  v.literal("collection"),
  /** Mensajes de una conexion MQTT (`target` = id de esa conexion). */
  v.literal("mqtt"),
);

export const validatorIntegrationTrigger = v.object({
  connectionId: v.id("integration_connections"),
  /** Redundante con la conexion, para consultas rapidas. */
  pluginId: v.string(),
  name: v.string(),
  trigger: v.object({
    source: integrationTriggerSource,
    /** Tabla del sistema (`invoices`), id de la coleccion o de la conexion MQTT. */
    target: v.string(),
    event: integrationTriggerEvent,
    /** Solo MQTT: filtro de topic (`planta/+/temp`, `#`). */
    topicFilter: v.optional(v.string()),
  }),
  action: v.object({
    actionId: v.string(),
    /** Args de la accion; los `template` admiten `{{record.campo}}`. */
    args: scalarRecord,
  }),
  /** Filtro: todas deben cumplirse (mismo DSL que las automatizaciones). */
  conditions: v.array(automationCondition),
  enabled: v.boolean(),
  lastRunAt: v.optional(v.number()),
  lastRunStatus: v.optional(v.union(v.literal("success"), v.literal("error"))),
  internalControl,
});

export const integration_triggers = defineTableWithInternalControl(validatorIntegrationTrigger)
  .index("by_company_and_target_event", [
    "internalControl.companyId",
    "trigger.target",
    "trigger.event",
  ])
  .index("by_connection", ["connectionId"]);

export const integrationExecutionStatus = v.union(
  v.literal("queued"),
  v.literal("running"),
  v.literal("success"),
  v.literal("error"),
  v.literal("skipped"),
  v.literal("retrying"),
);

export const validatorIntegrationExecution = v.object({
  connectionId: v.id("integration_connections"),
  triggerId: v.optional(v.id("integration_triggers")),
  pluginId: v.string(),
  actionId: v.string(),
  /** `flow`: paso `call_integration` de un flujo (`flowExecutionId`). */
  source: v.union(v.literal("trigger"), v.literal("script"), v.literal("manual"), v.literal("flow")),
  eventType: v.optional(integrationTriggerEvent),
  recordTable: v.optional(v.string()),
  recordRef: v.optional(v.string()),
  scriptId: v.optional(v.id("scripts")),
  /** Ejecucion de flujo que espera el resultado (`source: "flow"`). */
  flowExecutionId: v.optional(v.id("flow_executions")),
  /** Usuario que origino (actor del evento, autor del script o admin). */
  actorUserId: v.optional(v.id("users")),
  status: integrationExecutionStatus,
  /** JSON de los args ya resueltos (se reusa al reintentar). */
  args: v.string(),
  /** Respuesta del proveedor (JSON/texto truncado a 5 KB). */
  response: v.optional(v.string()),
  httpStatus: v.optional(v.number()),
  error: v.optional(v.string()),
  attempts: v.number(),
  nextAttemptAt: v.optional(v.number()),
  startedAt: v.number(),
  finishedAt: v.optional(v.number()),
  durationMs: v.optional(v.number()),
  internalControl,
});

export const integration_executions = defineTableWithInternalControl(validatorIntegrationExecution)
  .index("by_connection_and_started_at", ["connectionId", "startedAt"])
  .index("by_trigger_and_started_at", ["triggerId", "startedAt"])
  .index("by_company_and_status", ["internalControl.companyId", "status"])
  .index("by_started_at", ["startedAt"])
  /** Cola de comandos de los workers (MQTT publish). */
  .index("by_connection_and_status", ["connectionId", "status"]);

/** Limites. */
export const MAX_CONNECTIONS_PER_COMPANY = 20;
export const MAX_TRIGGERS_PER_CONNECTION = 30;
export const MAX_EXECUTION_ARGS_LENGTH = 20_000;
export const MAX_EXECUTION_RESPONSE_LENGTH = 5_000;
/** Reintentos automaticos: 1 min, 5 min, 30 min, 2 h, 12 h. */
export const RETRY_BACKOFF_MS = [60_000, 5 * 60_000, 30 * 60_000, 2 * 3_600_000, 12 * 3_600_000];
/** Las ejecuciones se purgan despues de estos dias. */
export const EXECUTION_RETENTION_DAYS = 30;
